import { api } from "../api-client.js";
import { renderEnvTabs, type Env } from "../components/env-tabs.js";
import { renderEmptyStateBanner, type ScreenState } from "../components/empty-state-banner.js";
import { bindDatePickerClick } from "../utils/date-input.js";
import { renderSortableHeaderRow, type SortDir } from "../utils/sortable-header.js";

type TradeSortBy = "ts" | "stock_name" | "realized_pnl" | "realized_pnl_pct" | "strategy_tag";

// 컬럼을 처음 클릭했을 때의 기본 방향 — 손익 계열은 큰 값부터(내림차순), 텍스트 계열(종목/조건식)은
// 사전순(오름차순)이 자연스럽다. 같은 컬럼을 다시 누르면 이 값과 무관하게 방향만 토글된다.
const SORT_DEFAULT_DIR: Record<TradeSortBy, SortDir> = {
  ts: "desc",
  stock_name: "asc",
  realized_pnl: "desc",
  realized_pnl_pct: "desc",
  strategy_tag: "asc",
};

interface TradeItem {
  ts: string;
  side: string | null;
  stock_code: string | null;
  stock_name: string | null;
  qty: number | null;
  price: number | null;
  amount: number | null;
  realized_pnl: number | null;
  realized_pnl_pct: number | null;
  strategy_tag: string | null;
  split_step: number | null;
  split_type: "익절" | "손절" | null;
}

interface TradesResult {
  env: Env;
  state: ScreenState;
  page: number;
  page_size: number;
  total: number;
  items: TradeItem[];
  total_buy_amount: number;
  total_sell_amount: number;
  total_realized_pnl: number;
}

function fmtTs(ts: string): string {
  if (ts.length !== 14) return ts;
  return `${ts.slice(0, 4)}-${ts.slice(4, 6)}-${ts.slice(6, 8)} ${ts.slice(8, 10)}:${ts.slice(10, 12)}`;
}

function fmtNum(v: number | null): string {
  return v === null ? "—" : v.toLocaleString("ko-KR");
}

// split_step 숫자만으로는 분할익절차수/분할손절차수(서로 독립된 카운터)를 구분할 수 없어
// split_type을 함께 표기한다(007 설계서 v1.1 — 0006_position_tracking.sql 마이그레이션 주석 참고).
function fmtSplitStep(step: number | null, type: "익절" | "손절" | null): string {
  if (step === null) return "—";
  return type ? `${step}차(${type})` : `${step}차`;
}

// 004 부록: 시간대는 Asia/Seoul(KST) 고정 — 백엔드 getKstDateString과 동일한 방식으로 프론트에서도
// "오늘" 날짜를 KST 기준으로 계산한다(브라우저 로컬 시간대와 무관하게).
function getKstDateString(): string {
  const kst = new Date(Date.now() + 9 * 60 * 60 * 1000);
  return kst.toISOString().slice(0, 10);
}

// 006 부록: KST 기준 순수 "달력 날짜" 산술 — 백엔드 performance-query.ts의 parseYmd/formatYmd/
// startOfIsoWeek와 정확히 같은 공식(주=월요일 시작)을 프론트 버전으로 그대로 옮겨온 것이다. 브라우저
// 로컬 시간대는 관여하지 않는다(입력이 이미 getKstDateString()이 만든 "YYYY-MM-DD" 문자열이므로).
function parseYmdUtc(dateStr: string): Date {
  const parts = dateStr.split("-").map(Number);
  const y = parts[0]!;
  const m = parts[1]!;
  const d = parts[2]!;
  return new Date(Date.UTC(y, m - 1, d));
}

function formatYmdUtc(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function startOfWeekKst(todayStr: string): string {
  const d = parseYmdUtc(todayStr);
  const day = d.getUTCDay(); // 0=일 .. 6=토
  const diff = day === 0 ? -6 : 1 - day; // 월요일 기준 — startOfIsoWeek(performance-query.ts)와 동일 공식
  d.setUTCDate(d.getUTCDate() + diff);
  return formatYmdUtc(d);
}

function startOfMonthKst(todayStr: string): string {
  return `${todayStr.slice(0, 7)}-01`;
}

function startOfYearKst(todayStr: string): string {
  return `${todayStr.slice(0, 4)}-01-01`;
}

type TradePeriodPreset = "" | "TODAY" | "WEEK" | "MONTH" | "YEAR"; // "" = 프리셋 없음(수동/전체)

// period → {from, to} 한 번에 계산. period === ""면 호출하지 않음(수동/전체 상태이므로).
function rangeForPeriod(period: Exclude<TradePeriodPreset, "">): { from: string; to: string } {
  const today = getKstDateString();
  switch (period) {
    case "TODAY":
      return { from: today, to: today };
    case "WEEK":
      return { from: startOfWeekKst(today), to: today };
    case "MONTH":
      return { from: startOfMonthKst(today), to: today };
    case "YEAR":
      return { from: startOfYearKst(today), to: today };
  }
}

// 사용자 피드백: 기간+매수/매도 필터를 잡아둔 채로 새로고침(사이드바 "JaTem" 클릭 = location.reload())
// 하면 필터가 다시 "전체"로 초기화돼서 불편했다 — env는 v1.5 안전 우선 원칙으로 항상 운영 기본값을
// 유지해야 하니 그대로 두고, side/기간/오늘만 필터만 localStorage에 저장해 새로고침 후에도 복원한다.
const TRADE_SORT_KEYS: TradeSortBy[] = ["ts", "stock_name", "realized_pnl", "realized_pnl_pct", "strategy_tag"];

interface TradesFilterState {
  side: "" | "BUY" | "SELL";
  from: string;
  to: string;
  period: TradePeriodPreset;
  sortBy: TradeSortBy;
  sortDir: SortDir;
  search: string;
}

const FILTER_STORAGE_KEY = "jatem_trades_filters";

function loadFilterState(): TradesFilterState {
  try {
    const parsed = JSON.parse(localStorage.getItem(FILTER_STORAGE_KEY) ?? "{}");
    const period: TradePeriodPreset =
      parsed.period === "TODAY" || parsed.period === "WEEK" || parsed.period === "MONTH" || parsed.period === "YEAR"
        ? parsed.period
        : "";
    return {
      side: parsed.side === "BUY" || parsed.side === "SELL" ? parsed.side : "",
      from: typeof parsed.from === "string" ? parsed.from : "",
      to: typeof parsed.to === "string" ? parsed.to : "",
      period,
      sortBy: TRADE_SORT_KEYS.includes(parsed.sortBy) ? parsed.sortBy : "ts",
      sortDir: parsed.sortDir === "asc" ? "asc" : "desc",
      search: typeof parsed.search === "string" ? parsed.search : "",
    };
  } catch {
    return { side: "", from: "", to: "", period: "", sortBy: "ts", sortDir: "desc", search: "" };
  }
}

function saveFilterState(state: TradesFilterState): void {
  try {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // 프라이빗 모드 등으로 localStorage를 못 쓰는 경우 — 필터 자체 동작에는 지장 없으니 조용히 무시.
  }
}

// 002 §7 화면4(거래내역): 체결 목록 테이블 + 필터 + 페이지네이션 + env 전환 탭.
export async function renderTrades(container: HTMLElement): Promise<void> {
  let env: Env = "운영";
  let page = 1;
  const savedFilters = loadFilterState();
  let side: "" | "BUY" | "SELL" = savedFilters.side;
  let period: TradePeriodPreset = savedFilters.period;
  let from = period ? rangeForPeriod(period).from : savedFilters.from;
  let to = period ? rangeForPeriod(period).to : savedFilters.to;
  let sortBy = savedFilters.sortBy;
  let sortDir = savedFilters.sortDir;
  let search = savedFilters.search;

  function persistFilters(): void {
    saveFilterState({ side, from, to, period, sortBy, sortDir, search });
  }

  const PERIOD_OPTIONS: Array<{ value: Exclude<TradePeriodPreset, "">; label: string }> = [
    { value: "TODAY", label: "오늘" },
    { value: "WEEK", label: "이번주" },
    { value: "MONTH", label: "이번달" },
    { value: "YEAR", label: "올해" },
  ];

  function selectPeriod(preset: Exclude<TradePeriodPreset, "">): void {
    if (period === preset) {
      // 켜져 있던 걸 다시 클릭 = 해제 → 전체(기존 "오늘만" 해제와 동일 동작)
      period = "";
      from = "";
      to = "";
    } else {
      period = preset;
      const r = rangeForPeriod(preset);
      from = r.from;
      to = r.to;
    }
    page = 1;
    persistFilters();
    void load();
  }

  function onSort(key: TradeSortBy): void {
    if (sortBy === key) {
      sortDir = sortDir === "asc" ? "desc" : "asc";
    } else {
      sortBy = key;
      sortDir = SORT_DEFAULT_DIR[key];
    }
    page = 1;
    persistFilters();
    void load();
  }

  // 사용자 피드백: 필터를 바꿀 때마다 화면 전체(제목·탭·필터까지)를 지웠다가 다시 그려서 데이터를
  // 기다리는 동안 화면이 깜빡였다 — 제목/탭/필터 UI는 슬롯에 담아 한 번만 배치하고, 응답이 와야
  // 바뀌는 결과 영역만 새 내용이 다 준비된 뒤 한 번에 교체한다(그 사이 이전 목록이 그대로 보임).
  container.innerHTML = `<h2 class="page-title">거래내역</h2>`;
  const tabsSlot = document.createElement("div");
  const toolbarSlot = document.createElement("div");
  const searchRowSlot = document.createElement("div");
  const dateRowSlot = document.createElement("div");
  const resultsSlot = document.createElement("div");
  container.append(tabsSlot, toolbarSlot, searchRowSlot, dateRowSlot, resultsSlot);

  function renderControls(): void {
    tabsSlot.replaceChildren(
      renderEnvTabs(env, (newEnv) => {
        env = newEnv;
        page = 1;
        void load();
      }),
    );

    // 기존엔 브라우저 기본 <select>라 너무 작고 안 눌리는 느낌이었다(사용자 피드백: "너무 작아,
    // 2배정도 키워줘") — 다른 화면(env-tabs 등)과 같은 큼직한 버튼 그룹으로 바꾸고 크게 키웠다.
    const SIDE_OPTIONS: Array<{ value: "" | "BUY" | "SELL"; label: string }> = [
      { value: "", label: "전체" },
      { value: "BUY", label: "매수" },
      { value: "SELL", label: "매도" },
    ];
    const toolbar = document.createElement("div");
    toolbar.className = "side-filter";
    for (const opt of SIDE_OPTIONS) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.textContent = opt.label;
      if (opt.value === side) btn.classList.add("active");
      btn.addEventListener("click", () => {
        side = opt.value;
        page = 1;
        persistFilters();
        void load();
      });
      toolbar.appendChild(btn);
    }
    toolbarSlot.replaceChildren(toolbar);

    // 사용자 요청: 종목명 또는 종목코드로 검색 — 백엔드가 stock_name/stock_code를 LIKE로 함께
    // 매칭해준다. 입력창에서 Enter로도 조회되게 한다(날짜 조회 버튼과 동일한 "누르면 조회" 패턴).
    const searchRow = document.createElement("div");
    searchRow.className = "custom-range-row";
    searchRow.innerHTML = `
      <input type="text" id="trades-search" placeholder="종목명 또는 종목코드 검색" value="${search}" />
      <button class="btn-secondary" id="trades-search-apply">검색</button>
      ${search ? `<button class="btn-secondary" id="trades-search-reset">전체보기</button>` : ""}
    `;
    const applySearch = () => {
      search = searchRow.querySelector<HTMLInputElement>("#trades-search")!.value.trim();
      page = 1;
      persistFilters();
      void load();
    };
    searchRow.querySelector<HTMLButtonElement>("#trades-search-apply")!.addEventListener("click", applySearch);
    searchRow.querySelector<HTMLInputElement>("#trades-search")!.addEventListener("keydown", (e) => {
      if (e.key === "Enter") applySearch();
    });
    searchRow.querySelector<HTMLButtonElement>("#trades-search-reset")?.addEventListener("click", () => {
      search = "";
      page = 1;
      persistFilters();
      void load();
    });
    searchRowSlot.replaceChildren(searchRow);

    // 날짜 범위 조회 (사용자 요청: 로그인 이력 화면과 같은 방식) — 백엔드 /api/trades는 이미
    // from/to를 지원했지만 화면엔 입력 UI가 없었다. "오늘만" 체크박스가 켜져 있는 동안은 날짜를
    // 직접 건드릴 이유가 없으니 입력/조회/전체보기를 비활성화해 두 모드가 충돌하지 않게 한다.
    const dateRow = document.createElement("div");
    dateRow.className = "custom-range-row";
    const anyPeriodActive = period !== "";
    dateRow.innerHTML = `
      <input type="date" id="trades-from" value="${from}" ${anyPeriodActive ? "disabled" : ""} />
      <span class="range-sep">~</span>
      <input type="date" id="trades-to" value="${to}" ${anyPeriodActive ? "disabled" : ""} />
      ${anyPeriodActive ? "" : `<button class="btn-secondary" id="trades-apply">조회</button>`}
      ${!anyPeriodActive && (from || to) ? `<button class="btn-secondary" id="trades-reset">전체보기</button>` : ""}
      <div class="period-check-group">
        ${PERIOD_OPTIONS.map(
          (opt) => `
          <label class="today-only-check">
            <input type="checkbox" data-period="${opt.value}" ${period === opt.value ? "checked" : ""} />
            ${opt.label}
          </label>`,
        ).join("")}
      </div>
    `;
    bindDatePickerClick(dateRow.querySelector<HTMLInputElement>("#trades-from")!);
    bindDatePickerClick(dateRow.querySelector<HTMLInputElement>("#trades-to")!);
    dateRow.querySelector<HTMLButtonElement>("#trades-apply")?.addEventListener("click", () => {
      from = dateRow.querySelector<HTMLInputElement>("#trades-from")!.value;
      to = dateRow.querySelector<HTMLInputElement>("#trades-to")!.value;
      page = 1;
      persistFilters();
      void load();
    });
    dateRow.querySelector<HTMLButtonElement>("#trades-reset")?.addEventListener("click", () => {
      from = "";
      to = "";
      page = 1;
      persistFilters();
      void load();
    });
    dateRow.querySelectorAll<HTMLInputElement>("input[data-period]").forEach((el) => {
      el.addEventListener("change", () => selectPeriod(el.dataset.period as Exclude<TradePeriodPreset, "">));
    });
    dateRowSlot.replaceChildren(dateRow);
  }

  async function load(): Promise<void> {
    renderControls();

    const params = new URLSearchParams({ env, page: String(page), sort_by: sortBy, sort_dir: sortDir });
    if (side) params.set("side", side);
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    if (search) params.set("q", search);

    let result: TradesResult;
    try {
      result = await api.get<TradesResult>(`/api/trades?${params.toString()}`);
    } catch {
      const err = document.createElement("p");
      err.textContent = "데이터를 불러오지 못했습니다.";
      resultsSlot.replaceChildren(err);
      return;
    }

    const frag = document.createDocumentFragment();

    const banner = renderEmptyStateBanner(result.state, env, (newEnv) => {
      env = newEnv;
      page = 1;
      void load();
    });
    if (banner) frag.appendChild(banner);

    // 조회 결과 총액: 여러 페이지로 나뉘어도 페이지 단위가 아닌 검색 조건(기간+구분) 전체 기준
    // 총액이어야 하므로 백엔드가 미리 집계해 준 total_buy_amount/total_sell_amount를 그대로 쓴다 —
    // 프론트에서 현재 페이지 items만 합산하면 안 됨(사용자 요청). 조회 영역 바로 아래(그리드 위)에
    // 둬야 눈에 잘 띈다는 사용자 요청으로 그리드보다 먼저 붙인다.
    if (result.items.length > 0) {
      const summary = document.createElement("div");
      summary.className = "trades-summary";
      const parts: string[] = [];
      if (side !== "SELL") {
        parts.push(`<span>매수 총액 <b class="tag-buy">${fmtNum(result.total_buy_amount)}</b></span>`);
      }
      if (side !== "BUY") {
        parts.push(`<span>매도 총액 <b class="tag-sell">${fmtNum(result.total_sell_amount)}</b></span>`);
        const pnlClass =
          result.total_realized_pnl > 0 ? "tag-buy" : result.total_realized_pnl < 0 ? "tag-sell" : "";
        parts.push(`<span>손익금 합계 <b class="${pnlClass}">${fmtNum(result.total_realized_pnl)}</b></span>`);
      }
      summary.innerHTML = parts.join("");
      frag.appendChild(summary);
    }

    const card = document.createElement("div");
    card.className = "card";

    if (result.items.length === 0) {
      card.innerHTML = `<p style="color:var(--text-dim)">표시할 체결 내역이 없습니다.</p>`;
    } else {
      const scrollWrap = document.createElement("div");
      scrollWrap.className = "table-scroll";
      const table = document.createElement("table");
      const thead = document.createElement("thead");
      thead.appendChild(
        renderSortableHeaderRow<TradeSortBy>(
          [
            { label: "시각", sortKey: "ts" },
            { label: "구분" },
            { label: "종목", sortKey: "stock_name" },
            { label: "수량" },
            { label: "단가" },
            { label: "금액" },
            { label: "손익", sortKey: "realized_pnl" },
            { label: "손익률", sortKey: "realized_pnl_pct" },
            { label: "조건식", sortKey: "strategy_tag" },
            { label: "매수차수" },
            { label: "매도차수" },
          ],
          sortBy,
          sortDir,
          onSort,
        ),
      );
      const tbody = document.createElement("tbody");
      tbody.innerHTML = result.items
        .map(
          (t) => `
        <tr>
          <td>${fmtTs(t.ts)}</td>
          <td class="${t.side === "BUY" ? "tag-buy" : "tag-sell"}">${t.side === "BUY" ? "매수" : t.side === "SELL" ? "매도" : "—"}</td>
          <td>${t.stock_name ?? "—"} <span style="color:var(--text-dim)">${t.stock_code ?? ""}</span></td>
          <td>${fmtNum(t.qty)}</td>
          <td>${fmtNum(t.price)}</td>
          <td>${fmtNum(t.amount)}</td>
          <td class="${(t.realized_pnl ?? 0) > 0 ? "tag-buy" : (t.realized_pnl ?? 0) < 0 ? "tag-sell" : ""}">${fmtNum(t.realized_pnl)}</td>
          <td>${t.realized_pnl_pct === null ? "—" : `${t.realized_pnl_pct.toFixed(2)}%`}</td>
          <td>${t.strategy_tag ?? "—"}</td>
          <td>${t.side === "BUY" ? fmtSplitStep(t.split_step, t.split_type) : "—"}</td>
          <td>${t.side === "SELL" ? fmtSplitStep(t.split_step, t.split_type) : "—"}</td>
        </tr>`,
        )
        .join("");
      table.appendChild(thead);
      table.appendChild(tbody);
      scrollWrap.appendChild(table);
      card.appendChild(scrollWrap);
    }
    frag.appendChild(card);

    const totalPages = Math.max(1, Math.ceil(result.total / result.page_size));
    const pagination = document.createElement("div");
    pagination.className = "pagination";
    const prevBtn = document.createElement("button");
    prevBtn.textContent = "이전";
    prevBtn.disabled = page <= 1;
    prevBtn.addEventListener("click", () => {
      page -= 1;
      void load();
    });
    const info = document.createElement("span");
    info.style.color = "var(--text-dim)";
    info.textContent = `${page} / ${totalPages} (총 ${result.total}건)`;
    const nextBtn = document.createElement("button");
    nextBtn.textContent = "다음";
    nextBtn.disabled = page >= totalPages;
    nextBtn.addEventListener("click", () => {
      page += 1;
      void load();
    });
    pagination.appendChild(prevBtn);
    pagination.appendChild(info);
    pagination.appendChild(nextBtn);
    frag.appendChild(pagination);

    resultsSlot.replaceChildren(frag);
  }

  await load();
}
